import React, { useState, useEffect } from "react";
import { Link, Redirect } from "react-router-dom";
import NumberFormat from "react-number-format";
import { API, graphqlOperation } from "aws-amplify";
import { parseJSON, format } from "date-fns";
import { es } from "date-fns/locale";
import AppBar from "@material-ui/core/AppBar";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Button from "@material-ui/core/Button";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import ForumIcon from "@material-ui/icons/Forum";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { getCartFull } from "../graphql/queries";
import { withContext } from "../context";
import logger from "../logger";
import { getPaymentTitle } from "../functions";
import { CART_STATUS, DELIVERY_OPTIONS } from "../constants";
import Loading from "../components/Loading";
import ImageLoader from "../components/ImageLoader";
import CartItem from "../components/CartItem";
import { defaultDeliveryConvenienceText } from "../components/PaymentWizard";

const Receipt = ({ cartID, store, useStoreSlug, history }) => {
  const [cart, setCart] = useState(null);
  const [redirect, setRedirect] = useState(null);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const vars = {
          id: cartID
        };
        const result = await API.graphql(graphqlOperation(getCartFull, vars));

        if (result.data.getCart) {
          const delivery = JSON.parse(result.data.getCart.delivery.details);
          const payment = JSON.parse(result.data.getCart.payment.details);
          const ticket = JSON.parse(result.data.getCart.ticket);
          const items = JSON.parse(result.data.getCart.items);

          const cart = {
            ...result.data.getCart,
            delivery: {
              ...result.data.getCart.delivery,
              details: delivery
            },
            payment: {
              ...result.data.getCart.payment,
              details: payment
            },
            ticket,
            items
          };

          setCart(cart);
        } else {
          setRedirect(useStoreSlug ? `/${store.slug}` : "/");
        }
      } catch (err) {
        logger("Receipt.fetchingCartFull.error", err.message);
      }
    };

    if (cartID) {
      fetchCart();
    }
  }, [cartID, store, useStoreSlug, history]);

  const continuar = () => {
    history.push(useStoreSlug ? `/${store.slug}` : "/");
  };

  const renderNoDelivery = () => {
    return (
      <div className="d-flex align-items-center">
        <ReportProblemOutlinedIcon fontSize="large" />
        <div className="d-flex flex-column ml-3">
          <Typography variant="body1">
            {`Producto${cart.items.length === 1 ? "" : "s"} sin delivery`}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Debe coordinar la entrega con el vendedor
          </Typography>
        </div>
      </div>
    );
  };

  const renderHomeDelivery = () => {
    const { details } = cart.delivery;
    let addressText = "";

    if (details.deliveryAddress.description) {
      addressText = details.deliveryAddress.description;
    } else {
      addressText =
        details.deliveryAddress.addressName +
        " " +
        details.deliveryAddress.addressNumber +
        ", " +
        details.deliveryAddress.addressComuna;
    }

    return (
      <div className="d-flex align-items-center">
        <LocalShippingOutlinedIcon fontSize="large" />
        <div className="d-flex flex-column ml-3">
          <Typography variant="body1">Entrega a domicilio</Typography>
          {details.deliveryService && (
            <Typography variant="body2">{details.deliveryService.name}</Typography>
          )}
          <Typography variant="body2" color="textSecondary">
            {addressText}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {details.deliveryAddress2}
          </Typography>
        </div>
      </div>
    );
  };

  const renderConvenience = () => {
    const { details } = cart.delivery;

    return (
      <div className="d-flex align-items-center">
        <ForumIcon fontSize="large" />
        <div className="d-flex flex-column ml-3">
          <Typography variant="body1">A convenir</Typography>
          <Typography variant="body2" color="textSecondary">
            {details.description ? details.description : defaultDeliveryConvenienceText}
          </Typography>
        </div>
      </div>
    );
  };

  const renderStorePickup = () => {
    const { details } = cart.delivery;

    return (
      <div className="d-flex align-items-center">
        <RoomOutlinedIcon fontSize="large" />
        <div className="d-flex flex-column ml-3">
          <Typography variant="body1">Retiro en tienda</Typography>
          <Typography variant="body2" color="textSecondary">
            {store.body.config.deliveryPickupText}
          </Typography>
          {details.pickupNote && (
            <Typography variant="body2" color="textSecondary">
              {details.pickupNote}
            </Typography>
          )}
          {details.pickupOption === "other" && details.pickupPerson && (
            <Typography variant="body2" color="textSecondary">
              Retirará {details.pickupPerson}
            </Typography>
          )}
        </div>
      </div>
    );
  };

  const renderDelivery = () => {
    switch (cart.delivery.type) {
      case DELIVERY_OPTIONS.delivery:
        return renderHomeDelivery();
      case DELIVERY_OPTIONS.pickup:
        return renderStorePickup();
      case DELIVERY_OPTIONS.convenience:
        return renderConvenience();
      case DELIVERY_OPTIONS.none:
        return renderNoDelivery();
      default:
        return null;
    }
  };

  const renderSummary = () => {
    return (
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="body2">Fecha</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" align="right">
            {format(parseJSON(cart.updatedAt), "Pp", {
              locale: es
            })}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2">Medio de pago</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" align="right">
            {getPaymentTitle(cart.payment.type)}
          </Typography>
        </Grid>

        {cart.status === CART_STATUS.finished && (
          <>
            {cart.payment.details.transaction && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Transacción</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" align="right">
                    {cart.payment.details.transaction.payment.transaction_id}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <Typography variant="body2">Comprobante</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">
                {cart.orderNumber}
              </Typography>
            </Grid>
          </>
        )}

        {cart.status === CART_STATUS.canceled && (
          <>
            <Grid item xs={6}>
              <Typography variant="body2">Comprobante</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className="text-danger" variant="body2" align="right">
                Rechazado
              </Typography>
            </Grid>
          </>
        )}

        {cart.status === CART_STATUS.onPayment && (
          <>
            <Grid item xs={6}>
              <Typography variant="body2">Estado</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className="text-info" variant="body2" align="right">
                Pendiente
              </Typography>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Divider className="my-2" />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2">Subtotal</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="body2" align="right">
            {
              <NumberFormat
                value={cart.payment.details.subtotal}
                thousandSeparator="."
                decimalSeparator=","
                prefix="$"
                displayType={"text"}
              />
            }
          </Typography>
        </Grid>

        {cart.delivery.type === DELIVERY_OPTIONS.delivery && cart.payment.details.deliveryFee > 0 && (
          <>
            <Grid item xs={6}>
              <Typography variant="body2">Despacho</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" align="right">
                <NumberFormat
                  value={cart.payment.details.deliveryFee}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="$"
                  displayType={"text"}
                />
              </Typography>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Divider className="my-2" />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="h6">Total del pedido</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" align="right">
            {
              <NumberFormat
                value={cart.payment.details.total}
                thousandSeparator="."
                decimalSeparator=","
                prefix="$"
                displayType={"text"}
              />
            }
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderProducts = () => {
    return (
      <List disablePadding dense>
        {cart.items.map((item, index) => (
          <React.Fragment key={`item-${index}`}>
            <CartItem index={index} item={item} />
            <Divider />
          </React.Fragment>
        ))}
      </List>
    );
  };

  if (!cart || !store) {
    return (
      <>
        <Loading fullscreen />
        {redirect && <Redirect to={redirect} />}
      </>
    );
  }

  return (
    <>
      <AppBar color="default" position="static" className="app-top-bar">
        <Toolbar>
          {store.logo ? (
            <ImageLoader imgKey={store.logo.key} className="logo" circularProgressSize={20} />
          ) : (
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/avatar.png`}
              className="logo"
              alt="Logo"
            />
          )}
          <Typography variant="h6" className="title">
            <Link to={useStoreSlug ? `/${store.slug}` : "/"}>{store.name}</Link>
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" className="mt-3 summary">
        <Typography variant="h5" className="mt-4">
          {cart.status === CART_STATUS.finished && (
            <span>{cart.client.firstname}, gracias por tu compra.</span>
          )}

          {cart.status === CART_STATUS.canceled && (
            <span>
              {cart.client.firstname}, este pedido quedó{" "}
              <span className="text-danger">cancelado</span>.
            </span>
          )}

          {cart.status === CART_STATUS.onPayment && (
            <span>{cart.client.firstname}, estamos esperando la confirmación del pago.</span>
          )}
        </Typography>

        {(cart.status === CART_STATUS.finished || cart.status === CART_STATUS.onPayment) && (
          <Typography variant="body1" className="mt-3">
            Número de pedido {cart.ticket.folio}
          </Typography>
        )}

        <Card className="mt-2 summary-products" elevation={0}>
          {renderProducts()}
        </Card>

        <Card className="summary-delivery receipt" elevation={0}>
          <CardContent>{renderDelivery()}</CardContent>
        </Card>

        <Divider variant="fullWidth" />

        <Card className="summary-payment" elevation={0}>
          <CardContent>{renderSummary()}</CardContent>
        </Card>

        <Divider variant="fullWidth" />

        <div className="d-flex justify-content-center">
          <Button className="my-4 wide" variant="contained" size="large" onClick={continuar}>
            Seguir comprando
          </Button>
        </div>
      </Container>
    </>
  );
};

export default withContext(Receipt);
